import PercentIcon from "@mui/icons-material/Percent";
import type { TDefaultAutocompleteOption } from "components/shared/FormComponent/AutocompleteFormComponent";
import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import NumberFormComponent from "components/shared/FormComponent/NumberFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import MessageBlock from "components/shared/MessageBlock";
import { socialProfileList } from "containers/NewBeneficiaryContainer";
import { PortalType } from "contexts/portal.context";
import RenderIf from "helpers/RenderIf";
import {
    getSocialSecurityPlaceholder,
    isSocialSecurityFormatMismatch,
    socialSecurityFormatAcceptsLetters,
} from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useVatPrecountWarning } from "hooks/useVatPrecountWarning";
import { t } from "i18next";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "store/store";
import { BeneficiaryVAT, EPortalType, TBeneficiaryPrecount } from "store/types";

import type { TBeneficiaryFormData } from "../types";

interface IProfileStep {
    /**
     * React useForm() hooks
     */
    useFormData: TBeneficiaryFormData;
    stepTitle: string;
    stepSubtitle: string;
    indexStep: number;
}

/**
 * Builds VAT options from the country features.
 * Labels come from the backend (i18n keys), translated here.
 * TWENTY_PERCENT is gated by the company setting uses_twenty_percent_vat.
 */
export const vatOptions = (countryVatOptions: { value: number; label: string }[], usesTwentyPercentVat: boolean) => {
    return countryVatOptions
        .filter((o) => o.value !== BeneficiaryVAT.TWENTY_PERCENT || usesTwentyPercentVat)
        .map((o) => ({ value: o.value, label: t(o.label) }));
};

const ProfileStep = ({ useFormData, stepTitle, stepSubtitle, indexStep }: IProfileStep) => {
    const [t] = useTranslation();
    const portalType = useContext(PortalType);
    const { companySettings } = useAppSelector((state) => state.settings);
    const { control, errors, watch } = useFormData;
    const { getCompanySettings, countryFeatures, companyCountryCode } = useCompanySettings();
    const usesTwentyPercentVat = getCompanySettings("uses_twenty_percent_vat");

    const hasVat = countryFeatures.vat.options.length > 0;
    const hasPrecount = countryFeatures.precount.options.length > 0;

    const precountList = countryFeatures.precount.options.map((o) => ({ value: o.value, label: t(o.label) }));

    const entity = watch("entity");
    const socialSecurityNumber = watch("socialSecurityNumber");

    const renderSocialProfileOption = (props: any, option: any) => (
        <li {...props} className="social-profile-list">
            <span>{option.type}</span>
            <div className="separator"></div>
            <span>{option.description}</span>
        </li>
    );

    const withheldPercentApplies =
        watch("vat")?.value === BeneficiaryVAT.FRANCHISE &&
        !["", "france"].includes((watch("country")?.name ?? "").toLowerCase());

    const vatPrecountWarningKey = useVatPrecountWarning(
        companyCountryCode,
        watch("vat")?.value as BeneficiaryVAT | undefined,
        watch("precount")?.value as TBeneficiaryPrecount | undefined,
    );

    const _vatOptions = vatOptions(countryFeatures.vat.options, usesTwentyPercentVat);

    const precountField =
        hasPrecount &&
        (countryFeatures.precountMode === "direct" ? (
            <AutocompleteFormComponent
                label={t("NewBeneficiary.Precount")}
                options={precountList}
                renderOptionText={(option: any) =>
                    precountList.find((o) => o.value === option.value)?.label ?? option.label
                }
                isOptionEqualToValue={(option: any, value: any) => option.value === value.value}
                defaultFilterOptions
                control={control}
                controlName="precount"
                required
                error={errors.precount}
                customRequiredMessage={t("NewBeneficiary.RequiredPrecount")}
            />
        ) : (
            <AutocompleteFormComponent
                label={t("NewBeneficiary.SocialProfile")}
                options={socialProfileList[(companySettings?.distributorType ?? 0) - 1]}
                renderOptionText={(option: any) => `${option.type} | ${option.description}`}
                defaultFilterOptions
                control={control}
                renderOption={renderSocialProfileOption}
                controlName="socialProfileType"
                required
                error={errors.socialProfileType}
                customRequiredMessage={t("NewBeneficiary.RequiredSocialProfile")}
            />
        ));

    const vatField = hasVat && (
        <AutocompleteFormComponent
            label={t("NewBeneficiary.VAT")}
            options={_vatOptions}
            renderOptionText={(option: TDefaultAutocompleteOption) =>
                _vatOptions.find((o) => o.value === option.value)?.label ?? option.label
            }
            isOptionEqualToValue={(option: TDefaultAutocompleteOption, value: TDefaultAutocompleteOption) =>
                option.value === value.value
            }
            defaultFilterOptions
            control={control}
            controlName="vat"
            required
            error={errors.vat}
        />
    );

    const ssnFormat = countryFeatures.validation.socialSecurityFormat;
    const isSsnWrong = isSocialSecurityFormatMismatch(ssnFormat, socialSecurityNumber);

    return (
        <>
            <h3 className="form-section-title">
                {indexStep + 1}. {stepTitle}
            </h3>
            <span className="form-section-description">{stepSubtitle}</span>
            <div className="form-fields">
                {hasVat && hasPrecount && (
                    <div className="form-component-wrapper half">
                        {precountField}
                        {vatField}
                    </div>
                )}
                {hasPrecount && !hasVat && precountField}
                {hasVat && !hasPrecount && vatField}
                {vatPrecountWarningKey && <MessageBlock type="warning" text={t(vatPrecountWarningKey)} small />}
                {withheldPercentApplies && (
                    <NumberFormComponent
                        label={t("Beneficiars.WithheldPercent")}
                        controlName="withheldPercent"
                        control={control}
                        error={errors.withheldPercent}
                        icon={<PercentIcon sx={{ width: "15px" }} />}
                        placeholder="0"
                    />
                )}
                {portalType === EPortalType.BOOK && (
                    <TextFormComponent
                        label={t("NewBeneficiary.ActivityType")}
                        control={control}
                        controlName="activityType"
                    />
                )}
                <RenderIf show={entity === "physical"}>
                    <div className="social-security-wrapper">
                        <TextFormComponent
                            label={t("Beneficiars.SecurityNumber")}
                            control={control}
                            controlName="socialSecurityNumber"
                            placeholder={getSocialSecurityPlaceholder(countryFeatures.validation.socialSecurityFormat)}
                        />
                        {isSsnWrong && (
                            <div className="form-field-warning">
                                {socialSecurityFormatAcceptsLetters(ssnFormat)
                                    ? t("ServerError.Field.socialSecurityNumber")
                                    : t("NewBeneficiary.FieldSeemInvalid", {
                                          field: t("Beneficiary.socialSecurityNumber"),
                                      })}
                            </div>
                        )}
                    </div>
                </RenderIf>
                <TextFormComponent
                    label={t("Beneficiars.Siret")}
                    control={control}
                    controlName="siretNumber"
                    placeholder={t("NewBeneficiary.CompanyCodeHere")}
                />
                <TextFormComponent
                    label={t("Beneficiars.Code")}
                    control={control}
                    controlName="beneficiaryCode"
                    placeholder={t("NewBeneficiary.CompanyCodeHere")}
                />
            </div>
        </>
    );
};

export default ProfileStep;
